import { ConfigProvider, Col, Row } from "antd";
import type { ThemeConfig } from "antd";
import { applyAffiliateTheme } from "@rumbledirect/ui-v2/theme";
import type { ReactNode } from "react";
import { Image } from "./components/Image";
import { ActionCard } from "./components/ActionCard";
import { DisplayCard } from "./components/DisplayCard";
import { UploadCard } from "./components/UploadCard";
import { Text } from "./components/Text";
import { MediaGallery } from "./components/MediaGallery";
import { InputCard } from "./components/InputCard";
import { ActionsCard } from "./components/ActionsCard";
import { LocationSelectorCard } from "./components/LocationSelectorCard";
import { HandInLocationSelectorCard } from "./components/HandInLocationSelectorCard";
import { PickupServiceCard } from "./components/PickupServiceCard";
import { ReplacementVehicleCard } from "./components/ReplacementVehicleCard";
import { AppointmentTimeSlotCard } from "./components/AppointmentTimeSlotCard";
import { CalculationUploadCard } from "./components/CalculationUploadCard";
import type { ViewComponent, ViewConfiguration } from "./types";

interface ViewRendererProps {
  view: ViewConfiguration;
  theme?: { cssVariables: CssVariables };
}

type ComponentRenderer = (component: ViewComponent) => ReactNode;

type CssVariables = Record<string, string | number>;

// The theme blob is an unvalidated key/value map; coerce each entry to the
// scalar type the antd token expects, ignoring values of the wrong type.
const stringValue = (
  cssVariables: CssVariables | undefined,
  key: string,
): string | undefined => {
  const value = cssVariables?.[key];
  return typeof value === "string" ? value : undefined;
};

const numberValue = (
  cssVariables: CssVariables | undefined,
  key: string,
): number | undefined => {
  const value = cssVariables?.[key];
  return typeof value === "number" ? value : undefined;
};

// One entry per ViewComponent type. Each function receives the union arm it
// is registered under, so props stay type-checked without a switch.
// Configurations are served from the database, so an unknown type renders
// nothing rather than throwing (see renderComponent below).
const renderers: Record<
  Exclude<ViewComponent["type"], "row">,
  ComponentRenderer
> = {
  image: (component) =>
    component.type === "image" ? (
      <Image
        key={component.key}
        src={component.src}
        alt={component.alt}
        maxWidth={component.maxWidth}
      />
    ) : null,
  actionCard: (component) =>
    component.type === "actionCard" ? (
      <ActionCard
        key={component.key}
        title={component.title}
        description={component.description}
        action={component.action}
      />
    ) : null,
  displayCard: (component) =>
    component.type === "displayCard" ? (
      <DisplayCard
        key={component.key}
        title={component.title}
        items={component.items}
        uiV2={component.uiV2}
      />
    ) : null,
  uploadCard: (component) =>
    component.type === "uploadCard" ? (
      <UploadCard
        key={component.key}
        title={component.title}
        description={component.description}
        uploadConfig={component.uploadConfig}
      />
    ) : null,
  text: (component) =>
    component.type === "text" ? (
      <Text key={component.key} value={component.value} />
    ) : null,
  mediaGallery: (component) =>
    component.type === "mediaGallery" ? (
      <MediaGallery
        key={component.key}
        title={component.title}
        items={component.items}
        uiV2={component.uiV2}
      />
    ) : null,
  inputCard: (component) =>
    component.type === "inputCard" ? (
      <InputCard
        key={component.key}
        title={component.title}
        description={component.description}
        fields={component.fields}
        submitEndpoint={component.submitEndpoint}
        submitText={component.submitText}
      />
    ) : null,
  actionsCard: (component) =>
    component.type === "actionsCard" ? (
      <ActionsCard
        key={component.key}
        title={component.title}
        description={component.description}
        image={component.image}
        actions={component.actions}
        uiV2={component.uiV2}
      />
    ) : null,
  locationSelectorCard: (component) =>
    component.type === "locationSelectorCard" ? (
      <LocationSelectorCard
        key={component.key}
        title={component.title}
        emptyState={component.emptyState}
        submitEndpoint={component.submitEndpoint}
        submitField={component.submitField}
        selectedName={component.selectedName}
        selectedAddress={component.selectedAddress}
      />
    ) : null,
  handInLocationSelectorCard: (component) =>
    component.type === "handInLocationSelectorCard" ? (
      <HandInLocationSelectorCard
        key={component.key}
        title={component.title}
        emptyState={component.emptyState}
        submitEndpoint={component.submitEndpoint}
        submitField={component.submitField}
        submitAddressField={component.submitAddressField}
        selectedName={component.selectedName}
        selectedAddress={component.selectedAddress}
      />
    ) : null,
  pickupServiceCard: (component) =>
    component.type === "pickupServiceCard" ? (
      <PickupServiceCard
        key={component.key}
        title={component.title}
        description={component.description}
        emptyState={component.emptyState}
        submitEndpoint={component.submitEndpoint}
        submitField={component.submitField}
        addressNameField={component.addressNameField}
        addressField={component.addressField}
        value={component.value}
        selectedName={component.selectedName}
        selectedAddress={component.selectedAddress}
      />
    ) : null,
  replacementVehicleCard: (component) =>
    component.type === "replacementVehicleCard" ? (
      <ReplacementVehicleCard
        key={component.key}
        title={component.title}
        description={component.description}
        emptyState={component.emptyState}
        submitEndpoint={component.submitEndpoint}
        submitField={component.submitField}
        commentField={component.commentField}
        value={component.value}
      />
    ) : null,
  appointmentTimeSlotCard: (component) =>
    component.type === "appointmentTimeSlotCard" ? (
      <AppointmentTimeSlotCard
        key={component.key}
        title={component.title}
        submitEndpoint={component.submitEndpoint}
        submitField={component.submitField}
        plannedStart={component.plannedStart}
      />
    ) : null,
  calculationUploadCard: (component) =>
    component.type === "calculationUploadCard" ? (
      <CalculationUploadCard
        key={component.key}
        title={component.title}
        buttonText={component.buttonText}
        submitEndpoint={component.submitEndpoint}
        dossierId={component.dossierId}
        calculation={component.calculation}
        uiV2={component.uiV2}
      />
    ) : null,
};

const renderComponent: ComponentRenderer = (component) => {
  if (component.type === "row") {
    return (
      <Row key={component.key} gutter={12}>
        {component.columns.map((column) => (
          <Col key={column.key} span={column.span} xxl={column.xxl}>
            {column.components.map(renderComponent)}
          </Col>
        ))}
      </Row>
    );
  }

  const render = renderers[component.type];

  if (!render) {
    // A deployed frontend can receive a type it does not have yet.
    if (import.meta.env.DEV) {
      console.warn(
        `ViewRenderer: no renderer registered for component type "${component.type}"`,
      );
    }
    return null;
  }

  return render(component);
};

export const ViewRenderer = ({ view, theme }: ViewRendererProps) => {
  const cssVariables = theme?.cssVariables;
  const primaryColor = stringValue(cssVariables, "--sjs-primary-backcolor");
  const borderRadius = numberValue(cssVariables, "--sjs-border-radius") ?? 4;
  const inputHeight = numberValue(cssVariables, "--sjs-input-height") ?? 48;

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: primaryColor,
          colorInfoBorder: primaryColor,
          colorInfoBg: "#e1ece8",
          colorLink: primaryColor,
          borderRadius,
          fontSize: 14,
          controlHeight: 40,
          fontFamily: "'Inter', sans-serif",
          colorBorderSecondary:
            stringValue(cssVariables, "--sjs-border-color-secondary") ??
            "#d8d8d8",
        },
        components: {
          Button: {
            paddingInline: 64,
            borderRadius: 50,
            fontSize: 16,
            fontWeight:
              stringValue(cssVariables, "--sjs-button-font-weight") ?? 600,
            controlHeight:
              numberValue(cssVariables, "--sjs-button-height") ?? 44,
            defaultShadow: "none",
            primaryShadow: "none",
          },
          Typography: {
            titleMarginBottom: "28px",
          },
          Alert: {
            borderRadiusLG: borderRadius,
          },
          DatePicker: {
            controlHeight: inputHeight,
            borderRadius,
            paddingInline: 16,
          },
          Input: {
            controlHeight: inputHeight,
            borderRadius,
            paddingInline: 16,
          },
          InputNumber: {
            controlHeight: inputHeight,
            borderRadius,
            paddingInline: 16,
          },
          Select: {
            controlHeight: inputHeight,
            paddingXS: 16,
            paddingSM: 16,
            borderRadius,
            paddingContentHorizontal: 16,
            optionSelectedFontWeight: 500,
          },
          // ponytail: headerPadding/bodyPadding are typed `number` in antd
          // 6's Card ComponentToken (same in antd 5; the apps compiled this
          // via their looser tsconfig), but the Card style generator emits
          // them into the CSS verbatim, so a multi-value padding shorthand
          // must be a string. The assertion widens exactly these two mistyped
          // keys and goes straight to antd's own slot type, so every other
          // token stays checked; drop it once antd types them correctly.
          Card: {
            borderRadiusLG: 16,
            headerPadding: "22px 0 42px",
            bodyPadding: "20px 42px 20px",
            headerFontSize: 18,
            headerHeight: 62,
            colorBorderSecondary: "#eee",
          } as unknown as NonNullable<ThemeConfig["components"]>["Card"],
        },
      }}
    >
      <div
        className="ui-v2-theme"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          ...applyAffiliateTheme(theme?.cssVariables),
        }}
      >
        {view.components?.map(renderComponent)}
      </div>
    </ConfigProvider>
  );
};
